import type { LeadSignal } from '../../../shared/types';
import { SIGNAL_EVENT_LABELS, SIGNAL_SOURCE_LABELS } from '../ui/labels';
import { diffDays, formatDate } from './dates';

/** Where a lead came from, in words (D96). One line per public record: what the record is, what it
 *  said, and how long ago it was filed — the answer to "why is this a lead?" while standing in
 *  front of the door. Pure: the view only puts these strings on the page. */

export interface ProvenanceLine {
  id: string;
  /** "ABC liquor license · New license" */
  what: string;
  /** "Filed Oct 10 · 14 days ago", or "Filed Oct 10" when the date cannot be read. */
  when: string;
  /** The record's own words, when it had any. */
  detail: string | null;
  /** A link to the record at its source, only ever http(s). */
  url: string | null;
  /** The filing the lead was made from; the others came in afterwards at the same door. */
  origin: boolean;
}

const safeUrl = (url: string | null): string | null => (url && /^https?:\/\//i.test(url) ? url : null);

/** A filing in the future is a date the source got wrong, or our own clock: say neither. */
const ago = (filed: string, today: string): string | null => {
  const days = diffDays(today, filed);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

export const provenanceLine = (s: LeadSignal, today: string): ProvenanceLine => {
  const since = ago(s.filed_on, today);
  const filed = `Filed ${formatDate(s.filed_on, today)}`;
  return {
    id: s.id,
    what: `${SIGNAL_SOURCE_LABELS[s.source]} · ${SIGNAL_EVENT_LABELS[s.event]}`,
    when: since ? `${filed} · ${since}` : filed,
    detail: s.detail,
    url: safeUrl(s.source_url),
    origin: s.status === 'added',
  };
};

export const provenanceLines = (signals: readonly LeadSignal[], today: string): ProvenanceLine[] =>
  signals.map((s) => provenanceLine(s, today));

/** What the section says above the list: a lead with no record was found on the street. */
export const provenanceSummary = (signals: readonly LeadSignal[]): string =>
  signals.length === 0
    ? 'No public record attached — this one came from the street.'
    : signals.length === 1
      ? 'One public record named this business.'
      : `${signals.length} public records named this business.`;
